import * as React from 'react';
import {Component, deps, inject, observer} from '../../lib/component';
import {style} from 'typestyle';
import {center, centerCenter, startJustified, vertical} from 'csstips';
import {px} from 'csx';
import {default as graphql, InjectedGraphQLProps} from 'react-apollo/lib/graphql';
import gql from 'graphql-tag';
import {SearchState} from '../../modules/page/search/model';
import {Loading} from '../loading/loading';
import {AddRemoveCompat} from '../compat/add-remove-compat';
import {BrandSponsoredLabelCompat} from '../compat/brand-sponsored-label-compat';
import {FavouriteButtonCompat} from '../compat/favourite-button-compat';
import {PlayButtonCompat} from '../compat/play-button-compat';
import {PaginationSelect} from '../pagination/select';
import {ReleaseTracks} from '../release-tracks';
import {PaginationMeta} from '../../types';
import {constantSearchFiltersWidth} from '../../theme/constant';
import {mediaTablet} from '../../theme/media';
import {zUnderLayer} from '../../theme/z';
import {colorWhite, colorContrast} from '../../theme/color';
import {TagResults, Track, TrackResults} from '../../types/graphql';
import {Pagination} from '../pagination';
import maybe from '../../lib/maybe';

interface SearchResultsProps extends SearchState {
  onClearClick?: () => void;
}

type PartialTagsFindResult = {
  matches: null | TagResults;
};

type SearchResultsData = {
  find: null | {
    tracks: null | TrackResults
  };
  genres: PartialTagsFindResult | null;
  themes: PartialTagsFindResult | null;
  moods: PartialTagsFindResult | null;
};

type CombinedProps = SearchResultsProps & InjectedGraphQLProps<SearchResultsData>;

@inject(deps) @observer
class SearchResults extends Component<CombinedProps> {
  render() {
    const { data, query } = this.props;
    const { isMobile } = this.props.model.ui;
    const { goToPage } = this.props.controller.page.search;
    const { preloadWaveformSvg } = this.props.controller.image;
    const { handleClickLink } = this.props.controller.ui;
    const isDrakeSearch = this.props.model.page.search.isDrakeSearch;

    const tracks = maybe<Track[]>(
      () => data!.find!.tracks!.results! || [],
      []
    );

    const pagination = maybe<Pagination>(
      () => data!.find!.tracks!.pagination!,
      null
    );

    const hasHits = tracks.length > 0;

    return (
      <div className={SearchResults.styles.container}>
        {data && data.loading && (
          <div className={SearchResults.styles.loadingOverlay}>
            <Loading/>
          </div>
        )}
        {isDrakeSearch && (
            <div className={SearchResults.styles.noResults}>
              Sorry, we don’t have Drake on the platform yet, how about one of these tracks?
            </div>
        )}
        {!hasHits && !isDrakeSearch && (
            <div className={SearchResults.styles.noResults}>
              Sorry, doesn't look like we have that yet but it's on our radar!
              Click <a href="#" onClick={this.handleClearClick}>here</a> to clear the search and try something else.
            </div>
        )}
        {hasHits && (
          <div className={SearchResults.styles.tracks}>
            <ReleaseTracks
              analytics={this.props.controller.analytics}
              AddRemove={AddRemoveCompat}
              BrandSponsoredLabel={BrandSponsoredLabelCompat}
              Favourite={FavouriteButtonCompat}
              PlayButton={PlayButtonCompat}
              isMobile={isMobile}
              onClickLink={handleClickLink}
              preloadWaveform={preloadWaveformSvg}
              model={this.releaseTracksModel(query, tracks)}
            />
          </div>
        )}
        {hasHits && pagination && (
          <div className={SearchResults.styles.pagination}>
            <PaginationSelect
              pagination={this.paginationMeta(pagination)}
              isMobile={isMobile}
              loadPage={page => goToPage(page - 1)}
            />
          </div>
        )}
      </div>
    )
  }

  handleClearClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (!this.props.onClearClick) {
      return;
    }

    this.props.onClearClick();
  };

  releaseTracksModel = (query: string, tracks: any[]) => ({
    loading: false,
    tracks,
    highlight: query,
  });

  paginationMeta = (meta: any): PaginationMeta => ({
    count: meta.total,
    current_page: meta.page + 1, // 0-index graphql, 1-index PaginationSelect
    per_page: meta.size,
    total: meta.total,
    total_pages: meta.totalPages
  });

  static styles = {
    container: style(
      {
        padding: 0,
        position: 'relative',
      },
      mediaTablet({
        marginRight: constantSearchFiltersWidth,
      })
    ),
    loadingOverlay: style({
      ...vertical,
      ...center,
      ...startJustified,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: zUnderLayer,
      padding: px(120),
      background: colorWhite.fade(0.9).toString()
    }),
    tracks: style({
      padding: '0 16px'
    }),
    pagination: style({
      ...centerCenter,
      padding: px(16)
    }),
    noResults: style({
      padding: px(30),
      $nest: {
          '& > a': {
            color: colorContrast.toString()
          }
      }
    })
  }
}

export const ConnectedSearchResults = graphql(gql`
  query (
    $query: String,
    $sortMode: FindSortMode,
    $sortOrder: FindSortOrder,
    $pagination: PaginationInput,
    $filters: FindFiltersInput,
  ) {
    find(
      query: $query,
      filters: $filters,
    ) {
      tracks(pagination: $pagination, sortOrder: $sortOrder, sortMode: $sortMode) {
        pagination {
          total
          totalPages
          from
          size
          page
        }
        results {
          identity
          highlight {
            key
            value
          }
          title
          slug
          duration
          created_at
          is_charting
          is_featured
          is_new_release
          brand_sponsored
          branded_content
          audio {
            identity
          }
          images {
            identity
          }
          artists {
            highlight {
              key
              value
            }
            name
            slug
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: SearchResultsProps) => ({
    variables,
  }),
})(SearchResults);